import { sameLinks } from '@lib/links'
import { placeToPayload, samePlacePayload } from '@lib/place'
import { GetFileType, PutEventType } from '@orbit/api-types'
import { Event } from '@types'
import { EventCreateDraft, draftFromEvent, eventName, filledLinks } from '../../EventCreateFlow/core/draft'

/**
 * Pure state for the edit-event flow: a create-event Draft seeded once from
 * the saved event, and the patch the edits map onto. Mirrors Android's
 * `EditEventState`, as the edit-destinations flow's state mirrors iOS's.
 */
export interface EventEditState {
	eventId: number
	/** The Draft as seeded — what every edit is measured against. */
	seeded: EventCreateDraft
	draft: EventCreateDraft
}

/** Seeds the editor from the event and the trip's files, of which it takes the event's own, in its order. */
export const seedEditState = (event: Event, tripFiles: GetFileType[]): EventEditState => {
	const byId = new Map(tripFiles.map(file => [file.id, file]))
	const savedFiles = event.fileIds.flatMap(id => byId.get(id) ?? [])
	const draft = draftFromEvent(event, savedFiles)
	return { eventId: event.id, seeded: draft, draft }
}

export const updateDraft = (state: EventEditState, draft: EventCreateDraft): EventEditState => ({ ...state, draft })

const locationOf = (draft: EventCreateDraft) => (draft.place && placeToPayload(draft.place)) ?? null

/**
 * Only the fields that changed. Each compares what the server would receive —
 * trimmed, blank links dropped — against the Draft as seeded rather than the
 * raw event, so a value the Draft reads differently (a padded name, a typed
 * location) isn't sent back unedited. A cleared field goes as null.
 */
export const toUpdatePayload = ({ seeded, draft }: EventEditState): PutEventType => {
	const patch: PutEventType = {}

	if (draft.tripDayIndex !== seeded.tripDayIndex) patch.tripDayIndex = draft.tripDayIndex

	const name = eventName(draft)
	if (name !== eventName(seeded)) patch.name = name

	if (draft.startTime !== seeded.startTime) patch.startTime = draft.startTime

	const description = draft.notes.trim()
	if (description !== seeded.notes.trim()) patch.description = description || null

	const location = locationOf(draft)
	if (!samePlacePayload(location, locationOf(seeded))) patch.location = location

	const links = filledLinks(draft)
	if (!sameLinks(links, filledLinks(seeded))) patch.tripEventLinks = links

	if (draft.category !== seeded.category) patch.category = draft.category

	return patch
}

/** The saved files taken off the event, to delete on Save. */
export const removedFileIds = ({ seeded, draft }: EventEditState): string[] => {
	const kept = new Set(draft.savedFiles.map(file => file.id))
	return seeded.savedFiles.filter(file => !kept.has(file.id)).map(file => file.id)
}

/** Whether a patch has anything in it worth sending. */
export const isEmptyPatch = (patch: PutEventType): boolean => Object.keys(patch).length === 0

/** Whether saving would do anything — gates both Save and the discard confirmation. */
export const hasChanges = (state: EventEditState): boolean =>
	!isEmptyPatch(toUpdatePayload(state)) ||
	removedFileIds(state).length > 0 ||
	state.draft.files.length > 0
